import React, { useEffect, useState } from 'react';
import { Oval } from 'react-loader-spinner';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube, FaGlobe, FaLink } from 'react-icons/fa';
import api from '../services/api';
import LazyImage from '../components/LazyImage';
import { Page, Header, HeaderCard, HeaderImage, Section, Card, Field, Button, InfoItem, SocialLinksRow, SocialIconLink } from './ContactStyles';

const Contact = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.contact.getPage()
      .then(data => { if (mounted) setPage(data); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true); setSent(null);
    try {
      await api.contact.createMessage(form);
      setSent('Message sent successfully');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setSent(err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Oval height={48} width={48} color="#2E8B57" secondaryColor="#CDEAD7" />
    </div>
  );
  if (error) return <div style={{ padding: 20 }}>Error: {error}</div>;

  const socialLinks = Array.isArray(page?.socialLinks) ? page.socialLinks : [];

  const getPlatformIcon = (platform) => {
    const key = (platform || '').toLowerCase();
    if (key === 'facebook') return FaFacebookF;
    if (key === 'twitter' || key === 'x') return FaTwitter;
    if (key === 'instagram') return FaInstagram;
    if (key === 'linkedin') return FaLinkedinIn;
    if (key === 'youtube') return FaYoutube;
    if (key === 'website') return FaGlobe;
    return FaLink;
  };

  const getPlatformLabel = (platform, fallback) => {
    const key = (platform || '').toLowerCase();
    if (key === 'facebook') return 'Facebook';
    if (key === 'twitter' || key === 'x') return 'Twitter';
    if (key === 'instagram') return 'Instagram';
    if (key === 'linkedin') return 'LinkedIn';
    if (key === 'youtube') return 'YouTube';
    if (key === 'website') return 'Website';
    return fallback || 'Social link';
  };

  return (
    <Page>
      <Header>
        <HeaderCard>
          <h1>{page?.title || 'Contact Us'}</h1>
          <p>{page?.description || ''}</p>
        </HeaderCard>
        <HeaderImage>
          {page?.image ? <LazyImage src={page.image} alt="Contact header" /> : null}
        </HeaderImage>
      </Header>

      <Section>
        <Card>
          <form onSubmit={onSubmit}>
            <Field>
              <label htmlFor="name">Name</label>
              <input id="name" name="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field>
              <label htmlFor="email">Email</label>
              <input id="email" name="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field>
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
            </Field>
            <Button type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send Message'}</Button>
            {sent ? <div style={{ marginTop: 10 }}>{sent}</div> : null}
          </form>
        </Card>

        <Card>
          <InfoItem><strong>Address:</strong> {page?.address || ''}</InfoItem>
          <InfoItem><strong>Email:</strong> {page?.email || ''}</InfoItem>
          <InfoItem><strong>Phone:</strong> {page?.phone || ''}</InfoItem>
          {page?.hotlinePhone ? <InfoItem><strong>Hotline:</strong> {page.hotlinePhone}</InfoItem> : null}
          {page?.hoursOfOperation ? <InfoItem><strong>Hours of Operation:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{page.hoursOfOperation}</span></InfoItem> : null}
          {socialLinks.length > 0 && (
            <>
              <InfoItem><strong>Follow us:</strong></InfoItem>
              <SocialLinksRow>
                {socialLinks.map((link, idx) => {
                  if (!link?.url) return null;
                  const Icon = getPlatformIcon(link.platform);
                  const label = getPlatformLabel(link.platform, link.label);
                  return (
                    <SocialIconLink
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      title={label}
                    >
                      <Icon />
                    </SocialIconLink>
                  );
                })}
              </SocialLinksRow>
            </>
          )}
        </Card>
      </Section>

      <Section style={{ gridTemplateColumns: '1fr', marginBottom: 24 }}>
        <Card style={{ padding: 0, overflow: 'hidden', height: 400 }}>
          <iframe
            title="Location Map"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0, minHeight: 400 }}
            src={`https://maps.google.com/maps?q=${encodeURIComponent(page?.address || 'Monrovia, Liberia')}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
            allowFullScreen
          />
        </Card>
      </Section>
    </Page>
  );
};

export default Contact;
